import {weakMemo} from '@packages/utilities/memo';
import {map as mapValues} from '@packages/utilities/key-map';
import {
  renderCssVariableName,
  type VariableMap,
  type AnyVariableType,
  type VariableCollectionMap,
} from '@packages/systems/core/utils/StyleVariables';

const webflowToTailwindVariables: Record<string, string> = {
  'Core Accent Color/Accent Primary': '--color-primary',
  'Core Accent Color/Accent Primary Hover': '--color-primary-hover',
  'Core Accent Color/Accent Secondary': '--color-secondary',
  'Core Accent Color/Accent Secondary Hover': '--color-secondary-hover',
  'Background Color/BG Primary': '--color-background',
  'Background Color/BG Inverse': '--color-background-inverse',
  'Text Color/Text Primary': '--color-foreground',
  'Text Color/Text Inverse Primary': '--color-foreground-inverse',
  'Border Color/Border Primary': '--color-border',
  'Text Color/Text On Accent Primary': '--color-foreground-primary',
  'Text Color/Text Secondary': '--color-foreground-secondary',
  'Text Link/Link Primary': '--color-text-link',
  'Text Link/Link Primary Hover': '--color-text-link-hover',
  'Text Link/Link Inverse': '--color-text-link-inverse',
  'Text Link/Link Inverse Hover': '--color-text-link-inverse-hover',

  'Radius/SM Radius': '--radius-sm',
  'Radius/MD Radius': '--radius-md',
  'Radius/LG Radius': '--radius-lg',
  'Radius/XL Radius': '--radius-xl',
  'Radius/Round': '--radius-full',
  'Spacing/0.25x': '--spacing',
  'Font/Body Font': '--font-body',
  'Font/Heading Font': '--font-heading',
  'Font/Button Font': '--font-button',
  'Text SM/SM Text Size': '--text-sm',
  'Text SM/SM Text Line Height': '--text-sm--line-height',
  'Text/Text Size': '--text-base',
  'Text/Text Line Height': '--text-base--line-height',
  'Text LG/LG Text Size': '--text-lg',
  'Text LG/LG Text Line Height': '--text-lg--line-height',
  'Text XL/XL Text Size': '--text-xl',
  'Text XL/XL Text Line Height': '--text-xl--line-height',
  'Text XXL/XXL Text Size': '--text-2xl',
  'Text XXL/XXL Text Line Height': '--text-2xl--line-height',
};

const semanticColorDefaults = {
  '--color-primary': 'hsl(262 83% 58% / var(--tw-bg-opacity, 1))', // purple-600
  '--color-primary-hover': 'hsl(262 72% 47% / var(--tw-bg-opacity, 1))', // purple-700
  '--color-secondary': 'hsl(292 84% 61% / var(--tw-bg-opacity, 1))', // fuchsia-500
  '--color-secondary-hover': 'hsl(292 76% 49% / var(--tw-bg-opacity, 1))', // fuchsia-600

  '--color-background': 'hsl(0 0% 100% / var(--tw-bg-opacity, 1))', // white
  '--color-background-inverse': 'hsl(255 44% 14% / var(--tw-bg-opacity, 1))', // violet-950-ish

  '--color-foreground': 'hsl(248 45% 10% / var(--tw-text-opacity, 1))', // indigo-950-ish
  '--color-foreground-inverse': 'hsl(0 0% 100% / var(--tw-text-opacity, 1))',

  '--color-border': 'hsl(220 13% 91% / var(--tw-border-opacity, 1))', // gray-200

  '--color-foreground-primary': 'hsl(248 45% 10% / var(--tw-text-opacity, 1))',
  '--color-foreground-secondary': 'hsl(220 9% 46% / var(--tw-text-opacity, 1))', // gray-500

  '--color-text-link': 'hsl(262 83% 58% / var(--tw-text-opacity, 1))',
  '--color-text-link-hover': 'hsl(262 72% 47% / var(--tw-text-opacity, 1))',
  '--color-text-link-inverse': 'hsl(270 100% 85% / var(--tw-text-opacity, 1))',
  '--color-text-link-inverse-hover':
    'hsl(270 100% 88% / var(--tw-text-opacity, 1))',

  '--font-body': 'DM Sans, ui-sans-serif, system-ui, "Inter", sans-serif',
  '--font-heading':
    'DM Serif Display, ui-sans-serif, system-ui, "Poppins", "Inter", sans-serif',
  '--font-button': 'DM Sans, ui-sans-serif, system-ui, "Inter", sans-serif',
};

export const projectVariablesToTailwindTheme = weakMemo(
  (variables: VariableMap) =>
    weakMemo((variableCollections: VariableCollectionMap) =>
      weakMemo((slugMap: Record<string, string>): string => {
        const variableNameIndex: Record<string, AnyVariableType> = {};
        for (const variable of variables.toArray()) {
          if (variable.deleted) continue;
          const tailwindName =
            webflowToTailwindVariables[
              variable.name as keyof typeof webflowToTailwindVariables
            ];
          if (!tailwindName) continue;
          variableNameIndex[tailwindName] = variable;
        }

        const cssVars = {
          ...semanticColorDefaults,
          ...mapValues((variable: AnyVariableType) => {
            return `var(--${renderCssVariableName(
              variables,
              variableCollections,
              slugMap,
              variable.id
            )})`;
          })(variableNameIndex),
        };

        const cssVarsWithDefault = Object.entries(cssVars).map(
          ([name, value]) => `${name}: ${value}`
        );

        return `@import "tailwindcss";
@theme inline {\n${cssVarsWithDefault.join(';\n')}\n}`;
      })
    )
);
